import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/RoleAssignment';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const ROLE_ASSIGNMENT_API_REDUCER_KEY = 'role-assignments';
export const ROLE_ASSIGNMENT_PATH = 'role-assignments';

export const roleAssignmentApi = createApi({
    reducerPath: ROLE_ASSIGNMENT_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['RoleAssignment', 'RoleAssignments'],
    endpoints: (builder) => ({
        getRoleAssignmentList: builder.query<PaginationResult<Entity.RoleAssignment>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ ROLE_ASSIGNMENT_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['RoleAssignments'],
        }),
        getRoleAssignment: builder.query<Entity.RoleAssignment, Entity.PrimaryRoleAssignment>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = roleAssignmentNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ ROLE_ASSIGNMENT_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['RoleAssignment'],
        }),
        getRoleAssignmentMultiple: builder.query<Entity.RoleAssignment, Entity.PrimaryRoleAssignment[]>({
            query: (ids) => {
                return ({
                    url: `/${ ROLE_ASSIGNMENT_PATH }/ids${Entity.PrimaryRoleAssignment.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['RoleAssignments'],
        }),
        getRoleAssignmentPresentation: builder.query<PaginationResult<Entity.RoleAssignment>, Entity.PresentationRoleAssignmentRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ ROLE_ASSIGNMENT_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['RoleAssignments'],
        }),
        getRoleAssignmentAdvancedPresentation: builder.query<PaginationResult<Entity.RoleAssignment>, Entity.FilterAdvancedPresentationRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
                return ({
                    url: `/${ ROLE_ASSIGNMENT_PATH }/find/advancedPresentation?${new URLSearchParams(queryParams).toString()}`,
                    method: 'POST',
                    data: request.filter,
                })
            },
            providesTags: ['RoleAssignments'],
        }),
        
        insertRoleAssignment: builder.mutation<Entity.RoleAssignment, Entity.CreateRoleAssignment>({
            query: (toInsert) => {
                return ({
                    url: `/${ ROLE_ASSIGNMENT_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    roleAssignmentApi.util.upsertQueryData('getRoleAssignment', Entity.PrimaryRoleAssignment.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['RoleAssignments'],
        }),
        insertMultipleRoleAssignment: builder.mutation<Entity.RoleAssignment[], Entity.CreateRoleAssignment[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ ROLE_ASSIGNMENT_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            roleAssignmentApi.util.upsertQueryData('getRoleAssignment', Entity.PrimaryRoleAssignment.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['RoleAssignments'],
        }),
        
        deleteRoleAssignment: builder.mutation<Entity.RoleAssignment, Entity.RoleAssignment>({
            query: (toDelete) => {
                return ({
                    url: `/${ ROLE_ASSIGNMENT_PATH }/${Entity.PrimaryRoleAssignment.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['RoleAssignments'],
        }),
        deleteMultipleRoleAssignment: builder.mutation<Entity.RoleAssignment[], Entity.PrimaryRoleAssignment[]>({
            query: (ids) => {
                return ({
                    url: `/${ ROLE_ASSIGNMENT_PATH }/batch${Entity.PrimaryRoleAssignment.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['RoleAssignments'],
        }),
    }),
})

export const [roleAssignmentNormalizer, roleAssignmentNormalizerMiddleware] = createNormalizationMiddleware(ROLE_ASSIGNMENT_API_REDUCER_KEY, roleAssignmentApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetRoleAssignmentListQuery,
    useGetRoleAssignmentMultipleQuery,
    useGetRoleAssignmentPresentationQuery,
    useGetRoleAssignmentQuery,
    useInsertRoleAssignmentMutation,
    useInsertMultipleRoleAssignmentMutation,
    
    useDeleteRoleAssignmentMutation,
    useDeleteMultipleRoleAssignmentMutation,
    useGetRoleAssignmentAdvancedPresentationQuery,
    
} = roleAssignmentApi
